import {
  goBack,
  HeaderCancelButtonComponent,
} from "components/molecules/Header";
import { colors } from "constants/colors";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  StyledText,
  StyledText10,
  StyledText20,
  StyledText30,
} from "styles/Text";
import styled, { css } from "styled-components";
import {
  useGetKidsGrowthShareEventList,
  useGetMobileWebEvent,
} from "api/queries";
import Comment from "components/molecules/Comment";
import PagingNav from "components/atoms/PagingNav";
import FullScreenModal from "components/molecules/FullScreenModal";
import KidsShareSubmit from "components/organisms/event/KidsShareSubmit";
import { useAlertStore } from "stores/alertStore";
import { isAuthRoleCheck } from "util/auth";
import { useHideBottomFixedForKeyboard } from "hooks/useHideBottomFixedForKeyboard";
import { IS_REAL } from "constants/api";
import { isBeforeTodayIgnoreToday } from "util/date";

const PRIMARY_COLOR = "#574CD7";
const SECONDARY_COLOR = "#615B9F";

// 우리 아이 성장 공유 이벤트 페이지
const KidsShare = () => {
  const GIFT_LIST: {
    name: string;
    count: number;
    image: string;
    isRandomColor?: boolean;
  }[] = [
    {
      name: "악스키드 원 카시트",
      count: 2,
      image: require("assets/images/event/kidsShare/img_snsshareevent_gift01.png"),
      isRandomColor: true,
    },
    {
      name: "스핀키드 에어셀 카시트",
      count: 1,
      image: require("assets/images/event/kidsShare/img_snsshareevent_gift02.png"),
      isRandomColor: true,
    },
    {
      name: "배달의민족 상품권 3만원권",
      count: 2,
      image: require("assets/images/event/kidsShare/img_snsshareevent_gift03.png"),
    },
    {
      name: "메가MGC커피 금액권 1만원권",
      count: 10,
      image: require("assets/images/event/kidsShare/img_snsshareevent_gift04_260521.png"),
    },
    {
      name: "파리바게뜨 모바일 금액권 5천원권",
      count: 15,
      image: require("assets/images/event/kidsShare/img_snsshareevent_gift05.png"),
    },
    {
      name: "CU 모바일 상품권 3천원권",
      count: 20,
      image: require("assets/images/event/kidsShare/img_snsshareevent_gift06.png"),
    },
  ];

  const { showAlert } = useAlertStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [isShowSubmitModal, setIsShowSubmitModal] = useState(false);

  const { data: mobileWebEventData, isSuccess } = useGetMobileWebEvent({
    randomKey: IS_REAL ? "ktfggu0d" : "fzguiqvm",
  });
  const { data: shareEventList, refetch } = useGetKidsGrowthShareEventList({
    page: currentPage,
    pageSize: 5,
  });
  const commentFocusScopeRef = useRef<HTMLElement | null>(null);
  const isInputFocused = useHideBottomFixedForKeyboard(commentFocusScopeRef);

  const isAuthorized = isAuthRoleCheck("ROLE_M");
  const eventId = mobileWebEventData?.eventId;
  const isEventEnd =
    !!mobileWebEventData?.endDt &&
    isBeforeTodayIgnoreToday(mobileWebEventData?.endDt);

  useEffect(() => {
    if (isEventEnd) {
      showAlert({
        message: "종료된 이벤트 입니다. 다음 기회에 참여해 주세요.",
        okButtonStyle: "gray",
        okOnPress: () => {
          goBack();
        },
      });
    }
  }, [isSuccess]);

  const closeSubmitModal = useCallback(() => {
    refetch();
    setIsShowSubmitModal(false);
  }, [refetch]);

  const goSelfCert = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "selfCert" }),
      );
    }
  };

  const onPressPageButton = (isLeft: boolean) => {
    if (isLeft) {
      setCurrentPage((p) => Math.max(1, p - 1));
    } else {
      setCurrentPage((p) =>
        Math.min(shareEventList?.pageInfo?.totalPage || 1, p + 1),
      );
    }
  };

  return (
    <>
      <HeaderCancelButtonComponent title="우리 아이 성장 공유 이벤트" />
      <StyledContent>
        <StyledVisualSection>
          <StyledContentInner>
            <StyledVisualSectionBox>
              <StyledShareEventMainTextImg
                src={require("assets/images/event/kidsShare/img_snsshareevent_maintext.png")}
                alt="열달후에 아이 성장 SNS 공유"
              />
              <StyledShareEventMainImg
                src={require("assets/images/event/kidsShare/img_snsshareevent_main.png")}
                alt="열달후에 공유 이벤트 이미지"
              />
            </StyledVisualSectionBox>
          </StyledContentInner>
        </StyledVisualSection>

        <StyledContentSection>
          <StyledContentInner>
            <StyledContentBox>
              <StyledContentTitleBox>
                <StyledText.Headline15 as="em">
                  공유만 해도 응모 완료
                </StyledText.Headline15>
                <StyledText30
                  color={PRIMARY_COLOR}
                  fontWeight="700"
                  $textAlign="center"
                  as="h2"
                  $lineHeight={36}
                >
                  성장 기록 공유하고
                  <br />
                  선물 받아가세요!
                </StyledText30>
                <StyledText.Body15 $textAlign="center" as="p">
                  엄마들 사이에서 입소문 난
                  <StyledText.Body15 fontWeight="500">
                    태아성장보고서 · 아이성장보고서
                  </StyledText.Body15>
                  를 공유해보세요.
                  <br />
                  <br />
                  추첨을 통해
                  <br />총 50명에게 푸짐한 선물을 드려요!
                </StyledText.Body15>
                <StyledTitleCard>
                  <StyledText.Sub12 $textAlign="center" color={SECONDARY_COLOR}>
                    많이 공유할 수록 당첨 확률 UP!
                    <br />
                    열달맘이라면 누구나 참여가능해요.
                  </StyledText.Sub12>
                </StyledTitleCard>
              </StyledContentTitleBox>

              <StyledContentImgBox>
                <StyledContentImg
                  src={require("assets/images/event/kidsShare/img_snsshareevent_gift_260521.png")}
                  alt="열달후에 아이 성장 SNS 공유"
                />
              </StyledContentImgBox>

              <StyledContentInfoBox>
                <StyledText.Headline15>
                  이벤트 기간｜
                  <StyledText.Body15
                    $textAlign="center"
                    as="p"
                    fontWeight="400"
                  >
                    2026.04.20 (월) ~ 2026.06.07 (일)
                  </StyledText.Body15>
                </StyledText.Headline15>
                <StyledText.Headline15>
                  당첨자 발표｜
                  <StyledText.Body15
                    $textAlign="center"
                    as="p"
                    fontWeight="400"
                  >
                    2026.06.10 (수)
                  </StyledText.Body15>
                </StyledText.Headline15>
              </StyledContentInfoBox>

              <StyledContentGiftBox>
                <StyledGiftList>
                  {GIFT_LIST.map((gift, index) => (
                    <StyledGiftItem key={gift.name}>
                      <StyledGiftItemImg
                        src={gift.image}
                        alt={`상품 이미지${index + 1}`}
                      />
                      <StyledGiftItemTextBox>
                        <StyledGiftTitle>
                          <StyledText.Headline15>
                            {gift.name}
                          </StyledText.Headline15>
                          {gift.isRandomColor && (
                            <StyledText10 color={colors.gray}>
                              *색상랜덤
                            </StyledText10>
                          )}
                        </StyledGiftTitle>
                        <StyledText.Sub12 color={colors.darkGray2}>
                          {gift.count}명
                        </StyledText.Sub12>
                      </StyledGiftItemTextBox>
                    </StyledGiftItem>
                  ))}
                </StyledGiftList>
              </StyledContentGiftBox>
            </StyledContentBox>
          </StyledContentInner>
        </StyledContentSection>

        <StyledMethodSection>
          <StyledContentInner>
            <StyledMethodBox>
              <StyledText.Headline15 as="em" $textAlign="center">
                SNS 공유 이벤트
              </StyledText.Headline15>
              <StyledText30
                color={PRIMARY_COLOR}
                fontWeight="700"
                $textAlign="center"
                as="h2"
                $lineHeight={36}
              >
                이벤트 참여 방법
              </StyledText30>

              <StyledMethodCard>
                <StyledText20 color={PRIMARY_COLOR} fontWeight="700">
                  01
                </StyledText20>
                <StyledMethodTitle>
                  <StyledText.Headline15 $textAlign="center">
                    사용 중인 '열달후에 화면'을 캡처해 주세요.
                  </StyledText.Headline15>
                  <StyledText.Body13
                    color={colors.subBody3}
                    $textAlign="center"
                  >
                    임신준비, 임신, 육아 모드 중<br />
                    자주 이용하는 1개의 화면을 선택하세요!
                  </StyledText.Body13>
                </StyledMethodTitle>

                <StyledMethodInfoImg
                  src={require("assets/images/event/kidsShare/img_snsshareevent_screen.png")}
                  alt="이벤트 참여 방법 이미지1"
                />

                <StyledMethodInfoList>
                  <StyledMethodInfoItem>
                    <StyledMethodInfoTitleBox>
                      <StyledText10
                        $lineHeight={12}
                        fontWeight="500"
                        color={colors.white}
                      >
                        임신준비 모드
                      </StyledText10>
                    </StyledMethodInfoTitleBox>
                    <StyledText.Sub11 $textAlign="center">
                      배테기체커, 임테기체커 등
                    </StyledText.Sub11>
                  </StyledMethodInfoItem>
                  <StyledMethodInfoItem>
                    <StyledMethodInfoTitleBox>
                      <StyledText10
                        $lineHeight={12}
                        fontWeight="500"
                        color={colors.white}
                      >
                        임신 모드
                      </StyledText10>
                    </StyledMethodInfoTitleBox>
                    <StyledText.Sub11 $textAlign="center">
                      태아성장보고서 상세기록, 주차기록,
                      <br />
                      종합보고서, 산모체중관리 등
                    </StyledText.Sub11>
                  </StyledMethodInfoItem>
                  <StyledMethodInfoItem>
                    <StyledMethodInfoTitleBox>
                      <StyledText10
                        $lineHeight={12}
                        fontWeight="500"
                        color={colors.white}
                      >
                        육아 모드
                      </StyledText10>
                    </StyledMethodInfoTitleBox>
                    <StyledText.Sub11 $textAlign="center">
                      육아성장보고서 성장기록, 성장추이,
                      <br />
                      종합보고서, 발달보고서, 산후체중관리 등
                    </StyledText.Sub11>
                  </StyledMethodInfoItem>
                </StyledMethodInfoList>
              </StyledMethodCard>
              <StyledMethodCard>
                <StyledText20 color={PRIMARY_COLOR} fontWeight="700">
                  02
                </StyledText20>
                <StyledMethodTitle>
                  <StyledText.Headline15 $textAlign="center">
                    캡처한 화면을 필수 해시태그와 함께
                    <br />
                    인스타, 블로그,맘카페 등<br />
                    SNS에 공유해 주세요!
                  </StyledText.Headline15>
                  <StyledText.Body13
                    color={colors.subBody3}
                    $textAlign="center"
                  >
                    [필수 해시태그]
                    <br />
                    #열달후에 #임신준비앱 #임신필수앱 #육아필수앱
                  </StyledText.Body13>
                </StyledMethodTitle>
                <StyledMethodShareImg
                  src={require("assets/images/event/kidsShare/img_snsshareevent_share.png")}
                  alt="이벤트 참여 방법"
                />
              </StyledMethodCard>
              <StyledMethodCard>
                <StyledText20 color={PRIMARY_COLOR} fontWeight="700">
                  03
                </StyledText20>
                <StyledMethodTitle>
                  <StyledText.Headline15 $textAlign="center">
                    ‘이벤트 참여하기’ 버튼을 누르고,
                    <br />
                    공유한 링크를 남기면 참여 완료!
                  </StyledText.Headline15>
                  <StyledText.Body13 color={colors.subBody3}>
                    많이 공유할수록 당첨 확률이 올라가요!
                  </StyledText.Body13>
                </StyledMethodTitle>

                <StyledMethodChanceBox>
                  {[1, 3, 10].map((chance) => (
                    <StyledText.Sub11
                      color={colors.subBody3}
                      key={`chance-${chance}`}
                    >
                      참여 {chance}회 → 추첨권 {chance}장
                    </StyledText.Sub11>
                  ))}
                </StyledMethodChanceBox>
              </StyledMethodCard>
            </StyledMethodBox>
          </StyledContentInner>
        </StyledMethodSection>

        <StyledShareEventListSection>
          <StyledContentInner>
            <StyledShareEventListBox>
              <StyledShareEventListTitle>
                <StyledText.Headline17
                  as="h3"
                  color={colors.white}
                  $textAlign="center"
                >
                  실시간 공유 및 참여 현황
                </StyledText.Headline17>
                <StyledShareEventSubTitleBox>
                  <StyledText.Sub12 color={colors.white}>
                    지금까지
                    {` ${shareEventList?.kidsGrowthShareEventList?.totalCount}`}
                    건이 공유 되었어요!
                  </StyledText.Sub12>
                </StyledShareEventSubTitleBox>
              </StyledShareEventListTitle>
              <StyledShareEventList>
                {shareEventList?.kidsGrowthShareEventList?.items?.map(
                  (item, index) => (
                    <StyledShareEventListItem key={`${item.usn}-${index}`}>
                      <StyledShareEventListItemTitle>
                        <StyledText.Headline12 color={colors.white}>
                          {item.nickNm}
                          <StyledText.Sub12 color={colors.subBody2}>
                            님이 이벤트에 참여했어요!
                          </StyledText.Sub12>
                        </StyledText.Headline12>
                        <StyledText10 color={colors.darkGray1}>
                          {item.createDtForSns}
                        </StyledText10>
                      </StyledShareEventListItemTitle>
                      <StyledShareEventStyledText10
                        color={colors.darkGray2}
                        title={item.snsUrl}
                      >
                        {item.snsUrl}
                      </StyledShareEventStyledText10>
                    </StyledShareEventListItem>
                  ),
                )}
              </StyledShareEventList>
              <PagingNav
                page={currentPage}
                totalCount={shareEventList?.pageInfo?.totalPage || 0}
                onClickPrev={() => onPressPageButton(true)}
                onClickNext={() => onPressPageButton(false)}
                $isFontWhite
              />
            </StyledShareEventListBox>
          </StyledContentInner>
        </StyledShareEventListSection>

        <StyledCommentSection ref={commentFocusScopeRef}>
          <StyledContentInner>
            <StyledCommentTitleBox>
              <StyledText.Headline17 $textAlign="center" as="h3">
                이벤트 응원의 댓글을 달아주세요!
              </StyledText.Headline17>
            </StyledCommentTitleBox>
          </StyledContentInner>
          {eventId && eventId > 0 && (
            <Comment svcId={eventId} isDisabled={isEventEnd} />
          )}
        </StyledCommentSection>

        <StyledInfoWrap>
          <StyledText.Headline15 as="h3" color={colors.subBody3}>
            이벤트 안내 및 유의사항
          </StyledText.Headline15>
          <StyledText.Body13
            as="p"
            color={colors.subBody3}
            style={{ whiteSpace: "pre-line" }}
          >
            {`[이벤트 안내]\n• 본 이벤트는 열달후에 회원 대상 이벤트입니다.\n• 비정상적인 방법으로 참여하거나 중복 응모가 확인될 경우 당첨이 취소될 수 있습니다.\n• 이벤트 참여 현황 및 내부 사정에 따라 당첨 인원, 경품 구성 및 지급 방식은 변경될 수 있습니다.\n• 이벤트 내용 및 일정은 내부 사정에 따라 변경 또는 조정될 수 있습니다.\n• 당첨자는 이벤트 종료 후 앱 내 공지사항 또는 개별 안내를 통해 발표됩니다.\n• 문의사항은 열달후에 고객센터로 문의 부탁드립니다.`}
          </StyledText.Body13>

          <StyledText.Body13
            as="p"
            color={colors.subBody3}
            style={{ whiteSpace: "pre-line" }}
          >
            {`[당첨자 유의사항]\n• 당첨자에게는 ‘열달후에’에서 본인인증 받으신 휴대전화번호로 경품이 발송되오니 인증 받으신 휴대전화번호를 꼭 확인해 주세요.\n• 인증받은 휴대전화번호 확인 방법 : 홈>마이메뉴>상단 프로필>내 정보 \n• 잘못된 휴대전화번호로 인한 경품 미수신에 대해서는 책임지지 않습니다.`}
          </StyledText.Body13>
        </StyledInfoWrap>

        <StyledFixedEngageBtn
          type="button"
          disabled={isEventEnd}
          $isInputFocused={isInputFocused}
          onClick={() => {
            if (!isAuthorized) {
              showAlert({
                titleMessage: "제목",
                message:
                  "당첨 시 경품 발송을 위해\n휴대전화번호 본인인증 후 참여 가능합니다.\n지금 인증 하시겠어요?",
                okOnPress: goSelfCert,
              });
            } else if (!window.ReactNativeWebView) {
              showAlert({
                message: "모바일이 아닙니다",
              });
            } else {
              setIsShowSubmitModal(true);
            }
          }}
        >
          이벤트 참여하기
        </StyledFixedEngageBtn>
      </StyledContent>

      {isShowSubmitModal && (
        <FullScreenModal onExitComplete={closeSubmitModal}>
          <KidsShareSubmit />
        </FullScreenModal>
      )}
    </>
  );
};

export default KidsShare;

const StyledContent = styled.main`
  min-height: 100vh;
  padding-top: 56px;
  background-color: ${colors.white};
  isolation: isolate;
`;

const StyledVisualSection = styled.section`
  background-color: #685ed6;
  padding-top: 40px;
`;

const StyledContentInner = styled.div`
  max-width: 360px;
  margin: 0 auto;
`;

const StyledMethodBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  padding: 0 20px;
`;

const StyledVisualSectionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledShareEventMainTextImg = styled.img`
  width: 100%;
  max-height: 218px;
  aspect-ratio: 267/218;
  object-fit: contain;
`;

const StyledShareEventMainImg = styled.img`
  width: 100%;
  max-height: 290px;
  aspect-ratio: 360/290;
  object-fit: contain;
`;

const StyledContentSection = styled.section`
  padding: 64px 0;
  background-color: #f3f2ff;
`;

const StyledContentBox = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  gap: 40px;
`;

const StyledContentTitleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 20px;
`;

const StyledContentImgBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledContentImg = styled.img`
  width: 100%;
  max-height: 300px;
  aspect-ratio: 320/300;
  object-fit: contain;
`;

const StyledTitleCard = styled.div`
  border-radius: 8px;
  padding: 8px 16px;
  border: 1px solid ${SECONDARY_COLOR};
`;

const StyledContentInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StyledContentGiftBox = styled.div``;

const StyledGiftList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  gap: 12px;
`;

const StyledGiftItem = styled.li`
  display: flex;
  flex-direction: row;
  background-color: ${colors.white};
  border-radius: 8px;
  padding: 12px;
  gap: 16px;
`;

const StyledGiftItemImg = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 4px;
`;

const StyledGiftItemTextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`;

const StyledGiftTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 2px;
  align-items: flex-end;
`;

const StyledMethodSection = styled.section`
  padding: 64px 0px 56px;
  background-color: ${colors.dimBg};
`;

const StyledMethodCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid ${PRIMARY_COLOR};
  border-radius: 12px;
  padding: 16px 12px 20px;
  gap: 12px;
`;

const StyledMethodTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
`;

const StyledMethodShareImg = styled.img`
  width: 100%;
  max-height: 169px;
  aspect-ratio: 296/169;
  object-fit: contain;
`;

const StyledMethodInfoImg = styled.img`
  width: 100%;
  max-height: 100px;
  aspect-ratio: 296/100;
  object-fit: contain;
`;

const StyledMethodInfoList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledMethodInfoItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const StyledMethodInfoTitleBox = styled.div`
  display: flex;
  padding: 2px 4px;
  border-radius: 2px;
  background-color: ${PRIMARY_COLOR};
  width: 63px;
  justify-content: center;
  align-items: center;
`;

const StyledMethodChanceBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  background-color: ${colors.subBody1};
`;

const StyledShareEventListSection = styled.section`
  padding: 64px 0px;
  background: #312d4e;
`;

const StyledShareEventListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 20px;
`;

const StyledShareEventListTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StyledShareEventSubTitleBox = styled.div`
  text-align: center;
  padding: 4px 8px;
  background-color: ${colors.subPurple4};
  border-radius: 4px;
`;

const StyledShareEventList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const StyledShareEventListItem = styled.li`
  display: flex;
  flex-direction: column;
  background-color: ${colors.black};
  padding: 12px;
  gap: 4px;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledShareEventListItemTitle = styled.div`
  display: flex;
  justify-content: space-between;

  & > *:first-child {
    flex: 1;
  }
  & > *:last-child {
    flex-shrink: 0;
    white-space: nowrap;
  }
`;

const StyledShareEventStyledText10 = styled(StyledText10)`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledCommentSection = styled.section`
  padding: 64px 0;
`;

const StyledCommentTitleBox = styled.div`
  text-align: center;
`;

const StyledFixedEngageBtn = styled.button<{ $isInputFocused: boolean }>`
  z-index: 3;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  box-sizing: border-box;
  width: 100%;
  border-radius: 12px 12px 0 0;
  background-color: ${colors.mainPurple};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.16);
  font-size: 16px;
  font-weight: 500;
  line-height: 56px;
  text-align: center;
  letter-spacing: -0.8px;
  color: ${colors.white};
  opacity: 1;
  transition: opacity 0.22s cubic-bezier(0.32, 0.72, 0, 1);
  &:disabled {
    background-color: ${colors.lightGray2};
    color: ${colors.white};
    cursor: not-allowed;
  }
  ${({ $isInputFocused }) =>
    $isInputFocused &&
    css`
      opacity: 0;
      pointer-events: none;
    `}
`;

const StyledInfoWrap = styled.div`
  display: flex;
  padding: 48px 20px 112px 20px;
  flex-direction: column;
  gap: 24px;
  background-color: ${colors.lightGray2};
`;
